/**
 * Bundle Quick Add
 *
 * Handles quick add-to-cart for bundle products from product cards.
 * Adds the complete bundle (Korb + Bügel + vorausgewählte Zubehörteile) to cart.
 *
 * Usage:
 * Add data-bundle-quick-add attribute to the form or button with required data attributes:
 * - data-bundle-config: JSON string with bundle configuration
 * - data-korb-variant-id: The selected Korb variant ID
 */

class BundleQuickAdd {
  constructor() {
    this.init();
  }

  init() {
    // Listen for form submissions with bundle data
    document.addEventListener('submit', this.handleFormSubmit.bind(this));

    // Listen for direct button clicks
    document.addEventListener('click', this.handleButtonClick.bind(this));
  }

  /**
   * Handle form submission for bundle quick add
   */
  handleFormSubmit(event) {
    const form = event.target;
    if (!form.hasAttribute('data-bundle-quick-add')) return;

    event.preventDefault();
    event.stopPropagation();

    const bundleConfig = this.getBundleConfig(form);
    if (bundleConfig) {
      this.addBundleToCart(bundleConfig, form);
    }
  }

  /**
   * Handle button click for bundle quick add
   */
  handleButtonClick(event) {
    const button = event.target.closest('[data-bundle-quick-add-btn]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const form = button.closest('form') || button;
    const bundleConfig = this.getBundleConfig(form);
    if (bundleConfig) {
      this.addBundleToCart(bundleConfig, button);
    }
  }

  /**
   * Get bundle configuration from element data attributes
   */
  getBundleConfig(element) {
    const configAttr = element.dataset.bundleConfig;
    if (!configAttr) {
      console.warn('BundleQuickAdd: No bundle config found');
      return null;
    }

    try {
      return JSON.parse(configAttr);
    } catch (e) {
      console.error('BundleQuickAdd: Invalid bundle config JSON', e);
      return null;
    }
  }

  /**
   * Generate a unique bundle ID for cart grouping
   */
  generateBundleId() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `lk_${timestamp}_${random}`;
  }

  /**
   * Add complete bundle to cart
   *
   * @param {Object} config Bundle configuration with:
   *   - korbVariantId: Selected Korb variant ID
   *   - buegelVariantId: Selected Bügel variant ID (optional)
   *   - accessories: Array of { variantId, vorausgewaehlt } for each accessory
   *   - productUrl: Product URL for config link
   */
  async addBundleToCart(config, triggerElement) {
    const { korbVariantId, buegelVariantId, accessories = [], productUrl = '' } = config;

    if (!korbVariantId) {
      console.warn('BundleQuickAdd: No Korb variant ID provided');
      return;
    }

    // Show loading state
    const button = triggerElement.tagName === 'BUTTON'
      ? triggerElement
      : triggerElement.querySelector('button[type="submit"]');

    if (button) {
      button.classList.add('btn--loading');
      button.disabled = true;
    }

    try {
      const bundleId = this.generateBundleId();
      const items = [];

      // 1. Add Korb (Parent item)
      const parentVariantId = parseInt(korbVariantId, 10);
      items.push({
        id: parentVariantId,
        quantity: 1,
        properties: {
          _lk_bundle_id: bundleId,
          _lk_config_url: productUrl,
        },
      });

      // 2. Add Bügel as child (if provided)
      if (buegelVariantId) {
        const buegelId = parseInt(buegelVariantId, 10);
        if (!isNaN(buegelId) && buegelId > 0) {
          items.push({
            id: buegelId,
            quantity: 1,
            parent_id: parentVariantId,
            properties: {
              _lk_bundle_id: bundleId,
            },
          });
        }
      }

      // 3. Add vorausgewählte accessories as children
      // Filter only vorausgewaehlt accessories
      const preselectedAccessories = accessories.filter(acc => acc.vorausgewaehlt === true);

      preselectedAccessories.forEach(acc => {
        const variantId = parseInt(acc.variantId, 10);
        if (!isNaN(variantId) && variantId > 0) {
          items.push({
            id: variantId,
            quantity: 1,
            parent_id: parentVariantId,
            properties: {
              _lk_bundle_id: bundleId,
            },
          });
        }
      });

      // console.log('BundleQuickAdd: Adding items:', items);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.description || data.message || 'Failed to add items to cart';
        throw new Error(errorMsg);
      }

      // Remove loading state
      if (button) {
        button.classList.remove('btn--loading');
        button.disabled = false;
      }

      // Dispatch cart:refresh event to update and open cart drawer
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          detail: { open: true },
        })
      );

      return data;
    } catch (error) {
      console.error('BundleQuickAdd: Error adding to cart:', error);

      // Remove loading state on error
      if (button) {
        button.classList.remove('btn--loading');
        button.disabled = false;
      }

      // Show error to user
      alert('Fehler beim Hinzufügen zum Warenkorb: ' + error.message);
    }
  }
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new BundleQuickAdd());
} else {
  new BundleQuickAdd();
}
